import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { apiService, Appointment } from '../../services/apiService';
import AppointmentModal from './AppointmentModal';
import AppointmentForm from './AppointmentForm';

const localizer = momentLocalizer(moment);

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: Appointment;
}

const CalendarView: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [showShareForm, setShowShareForm] = useState(false);

  useEffect(() => {
    loadAppointments();
  }, []);

  useEffect(() => {
    // Convert appointments to calendar events
    const calendarEvents: CalendarEvent[] = appointments.map(appointment => {
      const startDateTime = new Date(`${appointment.appointment_date}T${appointment.start_time}`);
      const endDateTime = new Date(`${appointment.appointment_date}T${appointment.end_time}`);
      
      return {
        id: appointment.id,
        title: `${appointment.patient_name} - ${appointment.procedure_name}`,
        start: startDateTime,
        end: endDateTime,
        resource: appointment
      };
    });
    
    setEvents(calendarEvents);
  }, [appointments]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAppointments();
      setAppointments(response.data.data || []);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedAppointment(event.resource);
    setShowModal(true);
  };

  const handleSelectSlot = (slotInfo: any) => {
    // Create a new appointment for the selected slot
    const newAppointment: Partial<Appointment> = {
      id: 0,
      patient_name: '',
      patient_email: '',
      patient_phone: '',
      appointment_date: moment(slotInfo.start).format('YYYY-MM-DD'),
      start_time: moment(slotInfo.start).format('HH:mm'),
      end_time: moment(slotInfo.end).format('HH:mm'),
      provider_id: 0,
      provider_name: '',
      procedure_id: 0,
      procedure_name: '',
      operatory_id: 0,
      operatory_name: '',
      status: 'scheduled' as const,
      notes: '',
      language: 'en',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    setEditingAppointment(newAppointment as Appointment);
    setShowForm(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAppointment(null);
  };

  const handleEditAppointment = () => {
    if (selectedAppointment) {
      setEditingAppointment(selectedAppointment);
      setShowForm(true);
      setShowModal(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAppointment(null);
  };

  const handleSaveAppointment = async () => {
    await loadAppointments();
    handleCloseForm();
  };

  const handleUpdateAppointment = async () => {
    await loadAppointments();
    handleCloseModal();
  };

  const handleGoogleCalendarSync = async () => {
    try {
      setSyncLoading(true);
      await apiService.syncGoogleCalendar();
      await loadAppointments();
      alert('Google Calendar sync completed successfully!');
    } catch (err) {
      console.error('Error syncing Google Calendar:', err);
      alert('Failed to sync with Google Calendar');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDebugGoogleCalendar = async () => {
    try {
      setDebugLoading(true);
      console.log('🔍 DEBUG: Fetching raw Google Calendar events...');
      
      const response = await apiService.debugGoogleCalendarEvents();
      
      console.log('🎉 DEBUG: Raw Google Calendar Response:');
      console.log('📊 Response Status:', response.status);
      console.log('📋 Response Data:', response.data);
      
      if (response.data.success) {
        console.log(`✅ Found ${response.data.events.length} events in Google Calendar`);
        console.log('📅 Calendar ID:', response.data.calendarId);
        console.log('🔐 Is Authenticated:', response.data.isAuthenticated);
        
        // Log each event in detail
        response.data.events.forEach((event: any, index: number) => {
          console.log(`\n📅 Event ${index + 1}:`);
          console.log('  🏷️ Title:', event.summary);
          console.log('  🆔 ID:', event.id);
          console.log('  ⏰ Start:', event.start);
          console.log('  ⏱️ End:', event.end);
          console.log('  📝 Description:', event.description?.substring(0, 200) + '...');
          console.log('  🔗 HTML Link:', event.htmlLink);
        });
        
        alert(`🎉 Debug complete! Found ${response.data.events.length} events. Check browser console for details.`);
      } else {
        console.error('❌ Debug failed:', response.data.error);
        alert(`❌ Debug failed: ${response.data.error}`);
      }
    } catch (err: any) {
      console.error('❌ Error debugging Google Calendar:', err);
      console.error('❌ Error details:', err.response?.data);
      alert(`❌ Debug error: ${err.message}`);
    } finally {
      setDebugLoading(false);
    }
  };

  const handleShareGoogleCalendar = async () => {
    if (!shareEmail.trim()) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      setShareLoading(true);
      console.log('🔗 Sharing calendar with:', shareEmail);
      
      const response = await apiService.shareGoogleCalendar(shareEmail, 'reader');
      
      console.log('✅ Calendar sharing response:', response.data);
      
      if (response.data.success) {
        alert(`✅ Calendar shared successfully with ${shareEmail}! They can now view events in their Google Calendar.`);
        setShareEmail('');
        setShowShareForm(false);
      } else {
        console.error('❌ Sharing failed:', response.data.error);
        alert(`❌ Sharing failed: ${response.data.error}`);
      }
    } catch (err: any) {
      console.error('❌ Error sharing calendar:', err);
      console.error('❌ Error details:', err.response?.data);
      
      // Provide more detailed error messages
      if (err.response?.status === 409) {
        alert(`📋 User ${shareEmail} already has access to the calendar.`);
      } else {
        alert(`❌ Sharing error: ${err.message}`);
      }
    } finally {
      setShareLoading(false);
    }
  };

  const eventStyleGetter = (event: CalendarEvent) => {
    const appointment = event.resource;
    let backgroundColor = '#3174ad';
    
    switch (appointment.status) {
      case 'confirmed':
        backgroundColor = '#10b981';
        break;
      case 'scheduled':
        backgroundColor = '#f59e0b';
        break;
      case 'completed':
        backgroundColor = '#3b82f6';
        break;
      case 'cancelled':
        backgroundColor = '#ef4444';
        break;
      case 'no_show':
        backgroundColor = '#6b7280';
        break;
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-admin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
          <p className="text-sm text-gray-600">Manage appointments and schedule</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleGoogleCalendarSync}
            disabled={syncLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {syncLoading ? 'Syncing...' : 'Sync Google Calendar'}
          </button>
          <button
            onClick={handleDebugGoogleCalendar}
            disabled={debugLoading}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
          >
            {debugLoading ? 'Debugging...' : 'Debug Google Events'}
          </button>
          <button
            onClick={() => setShowShareForm(!showShareForm)}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            Share Calendar
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            New Appointment
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Sharing Form */}
      {showShareForm && (
        <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
          <div className="flex flex-col space-y-3">
            <h3 className="text-sm font-medium text-orange-800">Share Google Calendar</h3>
            <p className="text-xs text-orange-700">
              Share the service account calendar with your personal Google account to view events directly in Google Calendar.
            </p>
            <div className="flex space-x-2">
              <input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="Enter your Google email address"
                className="flex-1 px-3 py-2 border border-orange-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button
                onClick={handleShareGoogleCalendar}
                disabled={shareLoading || !shareEmail.trim()}
                className="px-4 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
              >
                {shareLoading ? 'Sharing...' : 'Share'}
              </button>
              <button
                onClick={() => setShowShareForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Status Legend</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
            <span className="text-sm">Pending</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
            <span className="text-sm">Confirmed</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
            <span className="text-sm">Completed</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
            <span className="text-sm">Cancelled</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-gray-500 rounded mr-2"></div>
            <span className="text-sm">No Show</span>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow p-4">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          defaultView={Views.WEEK}
          step={30}
          showMultiDayTimes
          eventPropGetter={eventStyleGetter}
          formats={{
            timeGutterFormat: 'HH:mm',
            eventTimeRangeFormat: ({ start, end }) => {
              return `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`;
            }
          }}
        />
      </div>

      {/* Appointment Modal */}
      {showModal && selectedAppointment && (
        <AppointmentModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
          onUpdate={handleUpdateAppointment}
          onEdit={handleEditAppointment}
        />
      )}

      {/* Appointment Form */}
      {showForm && (
        <AppointmentForm
          appointment={editingAppointment || undefined}
          onClose={handleCloseForm}
          onSave={handleSaveAppointment}
        />
      )}
    </div>
  );
};

export default CalendarView; 